import { query, orderBy, where } from 'firebase/firestore';
import { useFirestoreQueryData } from '@react-query-firebase/firestore';
import { DR_Note } from '@rabbit/data/portal';
import { useEffect, useState } from 'react';
import { NoteEntityType, NoteStatus } from '@rabbit/data/types';

interface UseGetNotesProps {
  tenantLink: string;
  entityId: string;
  entityType: NoteEntityType;
  user_id?: string;
  isPartner?: boolean;
}

export function useGetNotes({
  tenantLink,
  entityId,
  entityType,
  user_id,
  isPartner,
}: UseGetNotesProps) {
  const [queryEnabled, setQueryEnabled] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                              Query Constraints                               */
  /* -------------------------------------------------------------------------- */

  const queryConstraints = [
    where('tenantLink', '==', tenantLink),
    where('entityId', '==', entityId),
    where('entityType', '==', entityType),
    where('status', '==', NoteStatus.ACTIVE),
    orderBy('tcreate', 'desc'), // Most recent notes first
  ];

  if (user_id && isPartner) {
    queryConstraints.push(where('updatedBy', '==', user_id));
  } else {
    queryConstraints.push(where('partnerTenantLink', '==', tenantLink));
  }

  /* ------------------------------ Create query ------------------------------ */
  const noteQuery = query(DR_Note.collection(), ...queryConstraints);

  const noteList = useFirestoreQueryData(
    ['notes', tenantLink, entityId, entityType],
    noteQuery,
    { subscribe: true },
    {
      enabled: queryEnabled,
      refetchOnWindowFocus: true,
      retry: true,
      onError: (err) => {
        console.error('Error fetching notes:', err);
      },
    }
  );

  const deleteNote = async (noteId: string) => {
    await DR_Note.delete(noteId);
  };

  useEffect(() => {
    const canRunQuery = () => {
      if (!tenantLink || !entityId || !entityType) return false;
      return true;
    };
    setQueryEnabled(canRunQuery());
  }, [tenantLink, entityId, entityType]);

  return {
    notes: noteList.data ?? [],
    deleteNote,
    isLoading: noteList.isLoading || queryEnabled === false,
  };
}
